import { requesterEmailsOf, type RequesterSource } from './changeRequester';
import {
    contactDisplayName,
    normalizeContactRole,
    type ClientContactLike,
} from './clientContacts';
import type { RequesterEmailSource } from './quickCreatePrefs';

const ACCOUNT_TYPE_BOT = 'bot';

const ROLE_SUGGESTION_LABEL: Record<string, string> = {
    facturation: 'Facturation',
    administratif: 'Administratif',
    technique: 'Technique',
};

type ClientLike = {
    email?: string | null;
    company?: string | null;
    firstname?: string | null;
    lastname?: string | null;
    name?: (() => string) | string | null;
    fullname?: (() => string) | string | null;
    contacts?: ClientContactLike[] | null;
};

type UserLike = {
    email?: string | null;
    account_type?: string | null;
    firstname?: string | null;
    lastname?: string | null;
    fullname?: (() => string) | string | null;
};

export type BuildRecipientEmailSourcesParams = {
    users?: UserLike[] | null;
    clients?: ClientLike[] | null;
    /** Current change and/or cards already loaded in Redux (demandeurs). */
    changes?: Array<RequesterSource | null | undefined> | null;
    /** Extra emails already known in the editor (cc, completionNotify, …). */
    extraEmails?: Array<string | null | undefined> | null;
};

function resolveLabel(value: (() => string) | string | null | undefined): string {
    if (typeof value === 'function') {
        try {
            return String(value() || '').trim();
        } catch {
            return '';
        }
    }
    return String(value || '').trim();
}

function pushSource(
    byEmail: Map<string, RequesterEmailSource>,
    email: string | null | undefined,
    label: string
) {
    const trimmed = String(email || '').trim();
    if (!trimmed)
        return;
    const key = trimmed.toLowerCase();
    if (byEmail.has(key))
        return;
    byEmail.set(key, { email: trimmed, label: label || trimmed });
}

function clientLabel(client: ClientLike): string {
    const named = resolveLabel(client.name);
    if (named)
        return named;
    const full = resolveLabel(client.fullname)
        || [client.firstname, client.lastname].filter(Boolean).join(' ').trim();
    return [client.company, full].filter(Boolean).join(' - ');
}

function userLabel(user: UserLike): string {
    const full = resolveLabel(user.fullname)
        || [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
    return full;
}

/**
 * Build a deduped email suggestion catalog from users, clients and known demandeurs.
 */
export function buildRecipientEmailSources(
    params: BuildRecipientEmailSourcesParams
): RequesterEmailSource[] {
    const byEmail = new Map<string, RequesterEmailSource>();

    (params.users || []).forEach((user) => {
        if (user?.account_type === ACCOUNT_TYPE_BOT)
            return;
        pushSource(byEmail, user?.email, userLabel(user));
    });

    (params.clients || []).forEach((client) => {
        pushSource(byEmail, client?.email, clientLabel(client));
        (client?.contacts || []).forEach((contact) => {
            const email = String(contact?.email || '').trim();
            if (!email)
                return;
            const roles = (contact.roles || [])
                .map((role) => ROLE_SUGGESTION_LABEL[normalizeContactRole(role)] || normalizeContactRole(role))
                .filter(Boolean);
            const person = contactDisplayName(contact);
            const base = clientLabel(client);
            const label = [base, roles.join(', '), person].filter(Boolean).join(' · ');
            pushSource(byEmail, email, label || email);
        });
    });

    (params.changes || []).forEach((change) => {
        if (!change)
            return;
        requesterEmailsOf(change).forEach((email) => pushSource(byEmail, email, email));
        (change.replyRecipients?.cc?.emails || []).forEach((email) => pushSource(byEmail, email, email));
        (change.completionNotify?.emails || []).forEach((email) => pushSource(byEmail, email, email));
    });

    (params.extraEmails || []).forEach((email) => pushSource(byEmail, email, String(email || '')));

    return Array.from(byEmail.values());
}
